import { usePaymentOrderService, useCreditPaymentService, usePaymentTypeService, useTaxActions, useTaxService, useExpenseTypeActions, useExpenseTypeHooks, useExpenseTypeService, useCashFundActions, useCashFundHooks, useCashFundService, useCheckActions, useCheckHooks, useCreditCardHooks, useCheckService, useCreditCardActions, useCreditCardService, useEmployeeHooks, useEmployeeActions, useEmployeeService, usePaymentTypeHooks, usePaymentTypeActions, useTaxHooks, useExpenseService } from "../../../modules/administration"
import { useVehicleActions, useVehicleHooks, useVehicleService, useDriverHooks, useDriverActions, useDriverService } from "../../../modules/logistics"
import { useProductHooks, useProductService, useCategoryHooks, useCategoryActions, useCategoryService } from "../../../modules/products"
import { useSupplierActions, useSupplierService, useSupplierHooks, usePurchaseService } from "../../../modules/purchases"
import { useCustomerTypeService, useCustomerActions, useCustomerHooks, useCustomerService, useCustomerTypeActions, useCustomerTypeHooks, useBudgetActions, useHoardService, useSaleService } from "../../../modules/sales"
import { DataType, TableName } from "../../../tables"
import { useAddressHooks } from "../../address"

export const useRemoveHooks = () => {
    const { canRemoveAddress } = useAddressHooks()
    const { canRemoveCashFund } = useCashFundHooks()
    const { canRemoveCategory } = useCategoryHooks()
    const { canRemoveCheck } = useCheckHooks()
    const { canRemoveCreditCard } = useCreditCardHooks()
    const { canRemoveCustomerType } = useCustomerTypeHooks()
    const { canRemoveCustomer } = useCustomerHooks()
    const { canRemoveDriver } = useDriverHooks()
    const { canRemoveEmployee } = useEmployeeHooks()
    const { canRemoveExpenseType } = useExpenseTypeHooks()
    const { canRemovePaymentType } = usePaymentTypeHooks()
    const { canRemoveProduct } = useProductHooks()
    const { canRemoveSupplier } = useSupplierHooks()
    const { canRemoveTax } = useTaxHooks()
    const { canRemoveVehicle } = useVehicleHooks()
    
    const { removeBudget } = useBudgetActions()
    const { removeCashFund } = useCashFundActions()
    const { removeCategory } = useCategoryActions()
    const { removeCheck } = useCheckActions()
    const { removeCustomerType } = useCustomerTypeActions()
    const { removeCustomer } = useCustomerActions()
    const { removeCreditCard } = useCreditCardActions()
    const { removeDriver } = useDriverActions()
    const { removeEmployee } = useEmployeeActions()
    const { removeExpenseType } = useExpenseTypeActions()
    const { removePaymentType } = usePaymentTypeActions()
    const { removeSupplier } = useSupplierActions()
    const { removeTax } = useTaxActions()
    const { removeVehicle } = useVehicleActions()
    
    const { archiveCashFund } = useCashFundService()
    const { archiveCategory } = useCategoryService()
    const { archiveCheck } = useCheckService()
    const { archiveCreditCard } = useCreditCardService()
    const { archiveCreditPayment } = useCreditPaymentService()
    const { archiveCustomerType } = useCustomerTypeService()
    const { archiveCustomer } = useCustomerService()
    const { archiveDriver } = useDriverService()
    const { archiveEmployee } = useEmployeeService()
    const { removeExpense } = useExpenseService()
    const { archiveExpenseType } = useExpenseTypeService()
    const { archiveHoard } = useHoardService()
    const { archivePaymentOrder } = usePaymentOrderService()
    const { archivePaymentType } = usePaymentTypeService()
    const { removeProduct, archiveProduct } = useProductService()
    const { archivePurchase } = usePurchaseService()
    const { archiveSale } = useSaleService()
    const { archiveSupplier } = useSupplierService()
    const { archiveTax } = useTaxService()
    const { archiveVehicle } = useVehicleService()

    const canAlwaysRemove = async () => true

    const canRemove = async (table: TableName, id: string) => {
        const canRemoveFn = new Map<TableName, (dataTypeId: string) => Promise<boolean>>()
            .set(TableName.ADDRESSES, canRemoveAddress)    
            .set(TableName.BUDGETS, canAlwaysRemove)
            .set(TableName.CASH_FUNDS, canRemoveCashFund)
            .set(TableName.CATEGORIES, canRemoveCategory)
            .set(TableName.CHECKS, canRemoveCheck)
            .set(TableName.CREDIT_CARDS, canRemoveCreditCard)
            .set(TableName.CUSTOMER_TYPES, canRemoveCustomerType)
            .set(TableName.CUSTOMERS, canRemoveCustomer)
            .set(TableName.DRIVERS, canRemoveDriver)
            .set(TableName.EMPLOYEES, canRemoveEmployee)
            .set(TableName.EXPENSES, canAlwaysRemove)
            .set(TableName.EXPENSE_TYPES, canRemoveExpenseType)
            .set(TableName.PAYMENT_TYPES, canRemovePaymentType)
            .set(TableName.PRODUCTS, canRemoveProduct)
            .set(TableName.SUPPLIERS, canRemoveSupplier)
            .set(TableName.TAXES, canRemoveTax)
            .set(TableName.VEHICLES, canRemoveVehicle)
        
        return canRemoveFn.get(table)?.(id) || false
    }

    const remove = async (table: TableName, id: string) => {
        const removeFn = new Map<TableName, (dataTypeId: string) => Promise<void>>()
            .set(TableName.BUDGETS, removeBudget)
            .set(TableName.CASH_FUNDS, removeCashFund)
            .set(TableName.CATEGORIES, removeCategory)
            .set(TableName.CHECKS, removeCheck)
            .set(TableName.CREDIT_CARDS, removeCreditCard)
            .set(TableName.CUSTOMER_TYPES, removeCustomerType)
            .set(TableName.CUSTOMERS, removeCustomer)
            .set(TableName.DRIVERS, removeDriver)
            .set(TableName.EMPLOYEES, removeEmployee)
            .set(TableName.EXPENSES, removeExpense)
            .set(TableName.EXPENSE_TYPES, removeExpenseType)
            .set(TableName.PAYMENT_TYPES, removePaymentType)
            .set(TableName.PRODUCTS, removeProduct)
            .set(TableName.SUPPLIERS, removeSupplier)
            .set(TableName.TAXES, removeTax)
            .set(TableName.VEHICLES, removeVehicle)
        
        return removeFn.get(table)?.(id)
    }

    const archive = async (table: TableName, id: string) => {
        const archiveFn = new Map<TableName, (dataTypeId: string) => Promise<DataType>>()
            .set(TableName.CASH_FUNDS, archiveCashFund)
            .set(TableName.CATEGORIES, archiveCategory)
            .set(TableName.CHECKS, archiveCheck)
            .set(TableName.CREDIT_CARDS, archiveCreditCard)
            .set(TableName.CREDIT_PAYMENTS, archiveCreditPayment)
            .set(TableName.CUSTOMER_TYPES, archiveCustomerType)
            .set(TableName.CUSTOMERS, archiveCustomer)
            .set(TableName.DRIVERS, archiveDriver)
            .set(TableName.EMPLOYEES, archiveEmployee)
            .set(TableName.EXPENSE_TYPES, archiveExpenseType)
            .set(TableName.HOARD_ORDERS, archiveHoard)
            .set(TableName.PAYMENT_ORDERS, archivePaymentOrder)
            .set(TableName.PAYMENT_TYPES, archivePaymentType)
            .set(TableName.PRODUCTS, archiveProduct)
            .set(TableName.PURCHASE_ORDERS, archivePurchase)
            .set(TableName.SALE_ORDERS, archiveSale)
            .set(TableName.SUPPLIERS, archiveSupplier)
            .set(TableName.TAXES, archiveTax)
            .set(TableName.VEHICLES, archiveVehicle)
        
        return archiveFn.get(table)?.(id)
    }
    
    return {
        canRemove,
        remove,
        archive
    }
}
